import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { ReactNode } from 'react';

import { getGroupStyles } from '@/sections/insights/queryBuilder/utils/filterStyles';
import {
  convertToLogicalFilter,
  isLogicalFilter,
  MAX_LOGICAL_DEPTH,
  shouldConvertToLogical,
} from '@/sections/insights/queryBuilder/utils/filterTypeGuards';
import type { QueryFilter } from '@/types';

import { LeafFilterCard } from './LeafFilterCard';

interface LogicalFilterGroupProps {
  filter: QueryFilter & { value: QueryFilter[] };
  index: number;
  depth: number;
  editable: boolean;
  onUpdate: (updates: Partial<QueryFilter>) => void;
  onDelete: () => void;
  renderFilter: (filter: QueryFilter, idx: number, depth: number) => ReactNode;
}

interface FilterWrapperProps {
  filter: QueryFilter;
  index: number;
  depth: number;
  editable: boolean;
  onUpdate: (updates: Partial<QueryFilter>) => void;
  onDelete: () => void;
  renderFilter: (filter: QueryFilter, idx: number, depth: number) => ReactNode;
}

const FilterWrapper = ({
  filter,
  index,
  depth,
  editable,
  onUpdate,
  onDelete,
  renderFilter,
}: FilterWrapperProps) => {
  // Render the appropriate component based on filter type
  if (isLogicalFilter(filter)) {
    return (
      <LogicalFilterGroup
        filter={filter as QueryFilter & { value: QueryFilter[] }}
        index={index}
        depth={depth}
        editable={editable}
        onUpdate={onUpdate}
        onDelete={onDelete}
        renderFilter={renderFilter}
      />
    );
  }

  return (
    <LeafFilterCard
      filter={filter}
      index={index}
      depth={depth}
      editable={editable}
      onUpdate={onUpdate}
      onDelete={onDelete}
    />
  );
};

export const LogicalFilterGroup = ({
  filter,
  index: _index,
  depth,
  editable,
  onUpdate,
  onDelete,
  renderFilter,
}: LogicalFilterGroupProps) => {
  const styles = getGroupStyles(depth);
  const children = filter.value as QueryFilter[];

  const handleAddChild = () => {
    const newChild: QueryFilter = { field: '', operator: '=', value: '' };
    onUpdate({ value: [...children, newChild] });
  };

  const handleDeleteChild = (childIndex: number) => {
    // If deleting last child, prompt to delete parent
    if (children.length === 1) {
      const confirmed = window.confirm(
        'This is the last filter in the group. Delete the entire group?'
      );
      if (confirmed) {
        onDelete();
      }
      return;
    }

    const updatedChildren = children.filter((_, idx) => idx !== childIndex);
    onUpdate({ value: updatedChildren });
  };

  const handleUpdateChild = (
    childIndex: number,
    updates: Partial<QueryFilter>
  ) => {
    const currentChild = children[childIndex];

    // Check if transitioning from leaf to logical operator
    if (shouldConvertToLogical(currentChild, updates)) {
      // Prevent creating logical filter if at max depth
      if (depth >= MAX_LOGICAL_DEPTH) {
        alert(
          `Maximum nesting depth (${MAX_LOGICAL_DEPTH} logical filters) reached. Cannot nest filters deeper.`
        );
        return;
      }

      // Validate operator before conversion
      if (updates.operator !== 'AND' && updates.operator !== 'OR') {
        console.error('Invalid logical operator:', updates.operator);
        return;
      }

      // Convert to logical filter with proper array initialization
      const converted = convertToLogicalFilter(updates.operator);
      const updatedChildren = [...children];
      updatedChildren[childIndex] = converted;
      onUpdate({ value: updatedChildren });
      return;
    }

    // Check if transitioning from logical to leaf operator (data loss warning)
    const wasLogical = isLogicalFilter(currentChild);
    const newOperator = updates.operator || currentChild.operator;
    const willBeLogical = newOperator === 'AND' || newOperator === 'OR';

    if (wasLogical && !willBeLogical) {
      const confirmed = window.confirm(
        'Converting this group to a single filter will delete all nested filters. Continue?'
      );
      if (!confirmed) {
        return;
      }

      // Convert logical to leaf filter
      const updatedChildren = [...children];
      updatedChildren[childIndex] = {
        field: '',
        operator: newOperator,
        value: '',
      };
      onUpdate({ value: updatedChildren });
      return;
    }

    // Standard update (no operator type change)
    const updatedChildren = [...children];
    updatedChildren[childIndex] = {
      ...updatedChildren[childIndex],
      ...updates,
    };
    onUpdate({ value: updatedChildren });
  };

  return (
    <div
      className={clsx(
        'space-y-2 rounded border p-3',
        styles.border,
        styles.background
      )}
    >
      {/* Header with operator dropdown/label and delete */}
      <div className="flex items-start justify-between">
        {editable ? (
          <select
            value={filter.operator}
            onChange={e =>
              onUpdate({ operator: e.target.value as 'AND' | 'OR' })
            }
            className={clsx(
              'rounded border-0 px-2 py-1 text-[9px] font-semibold',
              styles.labelBg,
              styles.labelText
            )}
            aria-label="Logical operator"
          >
            <option value="AND">AND</option>
            <option value="OR">OR</option>
          </select>
        ) : (
          <span
            className={clsx(
              'rounded px-2 py-1 text-[9px] font-semibold',
              styles.labelBg,
              styles.labelText
            )}
          >
            {filter.operator}
          </span>
        )}
        {editable && (
          <button
            onClick={onDelete}
            aria-label="Delete group"
            className="text-chariot-text-secondary hover:text-red-600"
          >
            <TrashIcon className="h-2.5 w-2.5" />
          </button>
        )}
      </div>

      {/* Children filters */}
      <div className="space-y-2">
        {children.map((child, idx) => (
          <FilterWrapper
            key={idx}
            filter={child}
            index={idx}
            depth={depth + 1}
            editable={editable}
            onUpdate={updates => handleUpdateChild(idx, updates)}
            onDelete={() => handleDeleteChild(idx)}
            renderFilter={renderFilter}
          />
        ))}
      </div>

      {/* Add child button */}
      {editable && (
        <button
          onClick={handleAddChild}
          className="inline-flex items-center gap-0.5 rounded px-2 py-0.5 text-[9px] text-blue-600 hover:bg-blue-50"
        >
          <PlusIcon className="h-2.5 w-2.5" />
          Add Filter
        </button>
      )}
    </div>
  );
};
